import { ChangeDetectionStrategy, ChangeDetectorRef, Component, signal, ViewChild, AfterViewInit, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { finalize, interval, Subscription, switchMap, takeWhile, tap } from 'rxjs';
import { DocumentsService } from 'src/app/core/service/api/documents.service';
import { PubsubService } from 'src/app/core/service/api/pubsub.service';
import { ChildTabbedPage } from 'src/app/core/util/abstract/child-tabbed-page';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { Globals } from 'src/app/globals';
import { SelectGeoJsonFileModalComponent } from 'src/app/shared/components/modal/select-geo-json-file-modal/select-geo-json-file-modal.component';
import { translate, TranslocoService } from '@jsverse/transloco';
import { ProjectConfigsService } from 'src/app/akita/project-configs/state/project-configs.service';
import { ImportFileModalComponent } from 'src/app/shared/components/modal/import-file-modal/import-file-modal.component';

@Component({
  selector: 'app-project-config-imports',
  templateUrl: './project-config-imports.component.html',
  styleUrl: './project-config-imports.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ProjectConfigImportsComponent extends ChildTabbedPage implements AfterViewInit {
  pageSubTitle: string;
  pageTitle = 'Import';
  tabName = 'import';
  searchTerm: string = '';
  selectedFile: any = null;
  loading = signal(false);
  selectedFileId: number | null = null;
  displayedColumns: string[] = ['select', 'fileName', 'date', 'status', 'details'];
  originalTableData: any[] = [];
  private subs = new Subscription();
  tableData = new MatTableDataSource<any>([]);
  @ViewChild('sortTable', { static: false }) sort!: MatSort;
  constructor(public globals: Globals, public pubsub: PubsubService, public dialog: MatDialog, private cdRef: ChangeDetectorRef, private _documentsService: DocumentsService, public _projectConfigsService: ProjectConfigsService, private translocoService: TranslocoService){
    super(pubsub, globals);
  }

  ngOnInit(): void {
    this.getSavedCustomFiles();
    this.subs.add(
      this._documentsService.refreshFiles$.subscribe(processId => {
        this.getSavedCustomFiles(true, processId);
      })
    );
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setupSorting();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
  setupSorting(): void {
    this.pageTitle = `${translate("admin_page.settings")} project ${ this.globals.project.name.toLowerCase()}`;
    this.init();
    if (this.sort) {
      this.tableData.sort = this.sort;
    }
    this.tableData.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'name':
          return item.name || '';
        case 'importDate':
          const date = item.importDate || item.created_at || item.createdAt;
          return date ? new Date(date).getTime() : 0;
        case 'status':
          return item.status || '';
        default:
          return item[property] || '';
      }
    };
  }

  onRowClick(element: any): void {
    const status = element.status?.toLowerCase();
    if (status === 'success' || status === 'failed' || status === 'partially-failed') {
      this.dialog.open(ImportFileModalComponent, modalConfig({
        data: { ...element },
        disableClose: true,
        panelClass: 'customized-dialog-container',
      }, ModalEnum.ModalMedium));
    }
  }


  getSavedCustomFiles(show:boolean=true, processId?:number|null){
    if (show)
      this.loading.set(true)
    this._documentsService.getSavedCustomFiles()
    .pipe(finalize(() => {
      this.loading.set(false);
    }))
    .subscribe({
      next: (res: any) => {
        const sortedData = this.sortByLatestDate(res);
        this.tableData.data = sortedData;
        this.originalTableData = sortedData;
        this.cdRef.detectChanges();
        if(processId)
          this.trackImportStatByProcessId(processId); 
        setTimeout(() => {
          this.setupSorting();
        }, 0);
      },
      error: (err) => {
        console.error('Error loading files', err);
      }
    });
  }

  private sortByLatestDate(files: any[]): any[] {
    return [...files].sort(
      (a, b) =>
        new Date(b.updated_at || b.created_at).getTime() -
        new Date(a.updated_at || a.created_at).getTime()
    );
  }

  public trackImportStatByProcessId(processId): void {
    interval(4000).pipe(
      switchMap(() => this._projectConfigsService.broadcastingProcess(processId)),
      takeWhile((res: any) => res.ok, true)
    ).subscribe({
      complete: () => this.getSavedCustomFiles(false, null)
    });
  }

  onSearchInput(event){
    const value = event.target.value.toLowerCase().trim();
    this.searchTerm = value;

    if (value === '') {
      // reset to full data
      this.tableData.data = [...this.originalTableData];
    } else {
      this.tableData.data = this.originalTableData.filter((file: any) =>
        file.name.toLowerCase().includes(value)
      );
    }

    this.cdRef.detectChanges();
  }

  onSelectRow(row: any, checked: boolean) {
    this.selectedFile = null;
    this.selectedFileId = checked ? row.id : null;
    this.tableData.data.forEach((f: any) => f.selected = f.id === this.selectedFileId);
  }

  onSelectFileDetail(file: any){
    this.selectedFile = file;
    this.cdRef.detectChanges();
  }

  downloadTemplate(file: string){
    let finalFileName = '';
    
    // Check if it's an Excel file and adjust based on language
    const currentLang = this.translocoService.getActiveLang();
    if (file === 'excel') {
      if (currentLang === 'nl') {
        finalFileName = 'excel_sjabloon.xlsx';
      } else {
        finalFileName = 'excel_template.xlsx';
      }
    } else if (file === 'geojson') {
      if (currentLang === 'nl') {
        finalFileName = 'geojson_sjabloon.geojson';
      } else {
        finalFileName = 'geojson_template.geojson';
      }
    }
    
    const fileUrl = `assets/import_templates/${finalFileName}`;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = finalFileName;
    link.click();
  }

  getStatusTranslationKey(status: string): string {
    if (!status) return '';
    const normalizedStatus = status.toLowerCase();
    const statusMap: { [key: string]: string } = {
      'success': 'imports.status_success',
      'failed': 'imports.status_failed',
      'warning': 'imports.status_warning',
      'processing': 'imports.status_processing',
      'partially-failed': 'imports.status_partially_failed'
    };
    return statusMap[normalizedStatus] || '';
  }

  splitFileName(fullName: string) {
    const regex = /^(.*?)-(\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2})?)$/;
    const match = fullName.match(regex);
    if (match) {
      return {
        fileName: match[1].trim(),
        dateTime: match[2].trim()
      };
    }
    return {
      fileName: fullName.trim(),
      dateTime: ''
    };
  }

  openSelectgeoJsonModal(){
    const dialog = this.dialog.open(SelectGeoJsonFileModalComponent, modalConfig({
      data: {type: 'excel'},
      panelClass: 'customized-dialog-container',
    }, ModalEnum.ModalMedium));
  }
}
